import { useState, useEffect } from "react";
import { useAuth, apiClient } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  BarChart3, 
  TrendingUp,
  Users,
  Ear,
  Stethoscope,
  Calendar,
  ArrowUpRight,
  ArrowDownRight
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";

export default function AnalyticsPage() {
  const { user, isSuperAdmin } = useAuth();
  const [overview, setOverview] = useState(null);
  const [conversion, setConversion] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [overviewRes, conversionRes] = await Promise.all([
        apiClient.get("/analytics/overview"),
        isSuperAdmin ? apiClient.get("/analytics/conversion") : Promise.resolve({ data: null })
      ]);
      setOverview(overviewRes.data);
      setConversion(conversionRes.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#0284c7', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444'];

  // Prepare clinic chart data
  const clinicChartData = overview?.clinic_stats?.slice(0, 8).map(c => ({
    name: c.clinic_name.replace("HearClear ", ""),
    patients: c.patients,
    tests: c.tests
  })) || [];

  // Prepare funnel data
  const funnelData = conversion ? [
    { name: "Leads", value: conversion.funnel.leads, color: "#0284c7" },
    { name: "Tested", value: conversion.funnel.tested, color: "#10b981" },
    { name: "Recommended", value: conversion.funnel.recommended, color: "#8b5cf6" },
    { name: "Trials", value: conversion.funnel.trials_scheduled, color: "#f59e0b" },
    { name: "Completed", value: conversion.funnel.trials_completed, color: "#22c55e" }
  ] : [];

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="analytics-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Analytics</h1>
          <p className="text-slate-500">Platform performance and insights</p>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { title: "Total Patients", value: overview?.total_patients || 0, icon: Users, color: "bg-sky-500" },
            { title: "Hearing Tests", value: overview?.total_tests || 0, icon: Ear, color: "bg-emerald-500" },
            { title: "Recommendations", value: overview?.total_recommendations || 0, icon: Stethoscope, color: "bg-violet-500" },
            { title: "Appointments", value: overview?.total_appointments || 0, icon: Calendar, color: "bg-amber-500" }
          ].map((stat, idx) => (
            <Card key={idx}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">{stat.title}</p>
                    <p className="text-3xl font-bold text-slate-900 mt-2">
                      {loading ? (
                        <span className="skeleton w-16 h-8 block"></span>
                      ) : (
                        stat.value.toLocaleString()
                      )}
                    </p>
                  </div>
                  <div className={`${stat.color} p-3 rounded-xl`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Conversion Funnel (Super Admin Only) */}
        {isSuperAdmin && conversion && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-sky-600" />
                  Conversion Funnel
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {funnelData.map((item, idx) => (
                    <div key={item.name} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-700">{item.name}</span>
                        <span className="text-sm text-slate-500">{item.value}</span>
                      </div>
                      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500"
                          style={{ 
                            width: `${(item.value / funnelData[0].value) * 100}%`,
                            backgroundColor: item.color
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Conversion Rates</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {[
                    { label: "Lead → Test", value: conversion.conversion_rates.lead_to_test },
                    { label: "Test → Recommendation", value: conversion.conversion_rates.test_to_recommendation },
                    { label: "Trial Completion", value: conversion.conversion_rates.trial_completion }
                  ].map((rate, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span className="text-slate-600">{rate.label}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-2xl font-bold text-slate-900">{rate.value}%</span>
                        {rate.value > 50 ? (
                          <ArrowUpRight className="w-5 h-5 text-green-500" />
                        ) : (
                          <ArrowDownRight className="w-5 h-5 text-amber-500" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Clinic Performance Chart */}
        {isSuperAdmin && clinicChartData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Clinic Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div style={{ height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={clinicChartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="name" 
                      angle={-45}
                      textAnchor="end"
                      height={80}
                      interval={0}
                      tick={{ fontSize: 12, fill: '#64748b' }}
                    />
                    <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Bar dataKey="patients" name="Patients" fill="#0284c7" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="tests" name="Tests" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-gradient-to-br from-sky-50 to-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-sky-600 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-sky-700">Active Clinics</p>
                  <p className="text-2xl font-bold text-sky-900">
                    {overview?.clinic_stats?.length || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-50 to-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center">
                  <Ear className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-emerald-700">Tests This Month</p>
                  <p className="text-2xl font-bold text-emerald-900">
                    {overview?.total_tests || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-violet-50 to-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-violet-600 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-violet-700">AI Recommendations</p>
                  <p className="text-2xl font-bold text-violet-900">
                    {overview?.total_recommendations || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
